import { useEffect, useRef } from "react";
import { KeyRound, ShieldCheck, X } from "lucide-react";
import { useTranslation } from "react-i18next";

// Предложение завести код-пароль перед показом реквизитов.
//
// Появляется ТОЛЬКО когда пина нет. Если он есть, спрашивать разрешение не о
// чем: запрос уходит сразу, упирается в 428 и человек видит привычный экран
// ввода — лишний шаг между «нажал» и «введи код» только раздражал бы.
//
// Лист, а не полный экран: человек нажал «Реквизиты» на конкретной карте, и
// карта должна остаться на виду. Полноэкранная заглушка вырывает из контекста
// и выглядит как ошибка, хотя это обычная развилка.
const SecureGateSheet = ({ onSetup, onClose }) => {
  const { t } = useTranslation();
  const primaryRef = useRef(null);

  // Фокус на действии, а не на крестике: клавиатура и скринридер должны
  // попадать на то, ради чего лист открыт.
  useEffect(() => {
    primaryRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[98] flex items-end justify-center sm:items-center">
      {/* Затемнение кликабельно — обычный способ отказаться, но своей кнопкой
          «Не сейчас» он не заменяется: у отказа должно быть видимое имя. */}
      <button
        type="button"
        className="lp-scrim--fixed"
        onClick={onClose}
        aria-label={t("common.close")}
        tabIndex={-1}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="secure-gate-title"
        className="lp-sheet relative w-full max-w-md px-5 pb-[calc(1.25rem+var(--lp-bottom))] pt-5 sm:rounded-[24px]"
        data-testid="secure-gate-sheet"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={t("common.close")}
          className="lp-press absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-[var(--lp-tile)] text-muted-foreground"
          data-testid="secure-gate-close"
        >
          <X className="h-4.5 w-4.5" />
        </button>

        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
          <ShieldCheck className="h-7 w-7 text-link" aria-hidden="true" />
        </div>

        <h2 id="secure-gate-title" className="mt-4 text-lg font-bold text-foreground">
          {t("cards.secure_gate_title")}
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          {t("cards.secure_gate_body")}
        </p>

        <button
          ref={primaryRef}
          type="button"
          onClick={onSetup}
          className="lp-press mt-5 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-[14px] bg-primary px-4 text-sm font-semibold text-primary-foreground"
          data-testid="secure-gate-setup"
        >
          <KeyRound className="h-4 w-4" aria-hidden="true" />
          {t("cards.secure_gate_cta")}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="lp-press mt-2 flex min-h-[48px] w-full items-center justify-center rounded-[14px] text-sm font-medium text-muted-foreground"
          data-testid="secure-gate-later"
        >
          {t("cards.secure_gate_later")}
        </button>
      </div>
    </div>
  );
};

export default SecureGateSheet;
